/**
 * Marketing-Menüstruktur (#870, Vorgabe Matthias 04.11.2026): Übersicht · Veröffentlichung · Produktion.
 * Einzige Quelle der Kanäle — ein Eintrag erzeugt automatisch die Unterseiten in BEIDEN Rubriken
 * (`/marketing/veroeffentlichung/<slug>` und `/marketing/produktion/<slug>`). Inhalte folgen später; heute nur Struktur.
 * Rechte: Cerbos-Ressourcen `marketing_veroeffentlichung` / `marketing_produktion` (Aktionen view, verwalten; portal-infra).
 */

export interface MarketingKanal {
  /** URL-Segment, nur a–z, 0–9 und Bindestrich */
  slug: string;
  /** Anzeigename */
  name: string;
  /** Icon-Kürzel (Platzhalter, bis Laura/Portaldesigner Icons festlegen) */
  kuerzel: string;
  /** Reihenfolge in Navigation und Kacheln (aufsteigend) */
  reihenfolge: number;
  /** inaktive Kanäle erscheinen nirgends und liefern 404 */
  aktiv: boolean;
}

export const MARKETING_KANAELE = [
  { slug: 'sea', name: 'SEA', kuerzel: 'SEA', reihenfolge: 10, aktiv: true },
  { slug: 'instagram', name: 'Instagram', kuerzel: 'IG', reihenfolge: 20, aktiv: true },
  { slug: 'tiktok', name: 'TikTok', kuerzel: 'TT', reihenfolge: 30, aktiv: true },
  { slug: 'pinterest', name: 'Pinterest', kuerzel: 'PI', reihenfolge: 40, aktiv: true },
  { slug: 'newsletter', name: 'Newsletter', kuerzel: 'NL', reihenfolge: 50, aktiv: true },
  { slug: 'facebook', name: 'Facebook', kuerzel: 'FB', reihenfolge: 60, aktiv: true },
  { slug: 'youtube', name: 'YouTube', kuerzel: 'YT', reihenfolge: 70, aktiv: true },
  { slug: 'ki-assistenten', name: 'KI-Assistenten', kuerzel: 'KI', reihenfolge: 80, aktiv: true },
] as const satisfies readonly MarketingKanal[];

export type MarketingRubrikKey = 'uebersicht' | 'veroeffentlichung' | 'produktion';

export interface MarketingRubrik {
  key: MarketingRubrikKey;
  label: string;
  path: string;
  /** Cerbos-Ressource der Rubrik (Übersicht = bestehendes Cockpit, Ressource `marketing`) */
  ressource: 'marketing' | 'marketing_veroeffentlichung' | 'marketing_produktion';
  /** 1-Satz-Beschreibung der Rubrik-Übersicht */
  beschreibung: string;
  /** hat Kanal-Unterseiten */
  kanaele: boolean;
}

export const MARKETING_RUBRIKEN = [
  { key: 'uebersicht', label: 'Übersicht', path: '/marketing', ressource: 'marketing', beschreibung: 'Kennzahlen aus Shop, Herkunft und SEO auf einen Blick.', kanaele: false },
  {
    key: 'veroeffentlichung', label: 'Veröffentlichung', path: '/marketing/veroeffentlichung', ressource: 'marketing_veroeffentlichung',
    beschreibung: 'Hier werden bereitgestellte Inhalte je Kanal geplant, veröffentlicht und ausgewertet.', kanaele: true,
  },
  {
    key: 'produktion', label: 'Produktion', path: '/marketing/produktion', ressource: 'marketing_produktion',
    beschreibung: 'Hier liefern berechtigte Personen und Agenten Videos, Posts, Newsletter und weitere Inhalte je Kanal zu und stellen sie bereit.', kanaele: true,
  },
] as const satisfies readonly MarketingRubrik[];

/** Aktive Kanäle in Anzeigereihenfolge */
export function aktiveKanaele(liste: readonly MarketingKanal[] = MARKETING_KANAELE): MarketingKanal[] {
  return liste.filter((k) => k.aktiv).sort((a, b) => a.reihenfolge - b.reihenfolge);
}

export function kanalPfad(rubrik: 'veroeffentlichung' | 'produktion', slug: string): string {
  return `/marketing/${rubrik}/${slug}`;
}

/** 1-Satz-Beschreibung einer Kanal-Unterseite */
export function kanalBeschreibung(rubrik: 'veroeffentlichung' | 'produktion', kanal: Pick<MarketingKanal, 'name'>): string {
  return rubrik === 'veroeffentlichung'
    ? `Hier stehen künftig die geplanten und veröffentlichten ${kanal.name}-Inhalte mit ihren Kennzahlen.`
    : `Hier stehen künftig die ${kanal.name}-Inhalte in Arbeit und die zur Veröffentlichung bereitgestellten Lieferungen.`;
}

export type MarketingRoute =
  | { rubrik: MarketingRubrikKey; kanal: null }
  | { rubrik: 'veroeffentlichung' | 'produktion'; kanal: MarketingKanal };

/**
 * Pfad → Rubrik/Kanal; `null` = unbekannt (Portal-404). Abschließender Schrägstrich wird ignoriert,
 * Groß-/Kleinschreibung nicht (Slugs sind klein).
 */
export function marketingRoute(pathname: string, liste: readonly MarketingKanal[] = MARKETING_KANAELE): MarketingRoute | null {
  const teile = (pathname.replace(/\/+$/, '') || '/').split('/').filter(Boolean);
  if (teile[0] !== 'marketing') return null;
  if (teile.length === 1) return { rubrik: 'uebersicht', kanal: null };
  const rubrik = teile[1];
  if (rubrik !== 'veroeffentlichung' && rubrik !== 'produktion') return null;
  if (teile.length === 2) return { rubrik, kanal: null };
  if (teile.length > 3) return null;
  const kanal = aktiveKanaele(liste).find((k) => k.slug === teile[2]);
  return kanal ? { rubrik, kanal } : null;
}

/** Alle Routen der Menüstruktur (Übersicht, 2 Rubrik-Übersichten, je aktivem Kanal 2 Unterseiten) */
export function marketingRouten(liste: readonly MarketingKanal[] = MARKETING_KANAELE): string[] {
  const k = aktiveKanaele(liste);
  return [
    '/marketing',
    '/marketing/veroeffentlichung',
    ...k.map((x) => kanalPfad('veroeffentlichung', x.slug)),
    '/marketing/produktion',
    ...k.map((x) => kanalPfad('produktion', x.slug)),
  ];
}
